/**
 * Salpido – PredictiveSearch & Dropdown Controller
 * Supports integrated desktop search pill dropdown and mobile search popover
 */

function animateKeyword(el, newText, callback) {
  el.classList.add('slide-out');
  setTimeout(function () {
    el.textContent = newText;
    el.classList.remove('slide-out');
    if (callback) callback();
  }, 200);
}

function initScrollingPlaceholders() {
  document.querySelectorAll('.scrolling-placeholder').forEach((ph) => {
    var raw = ph.dataset.keywords || '';
    var speed = parseInt(ph.dataset.speed, 10) || 2500;
    var keywords = raw.split(',').map((k) => k.trim()).filter(Boolean);
    if (keywords.length < 2) return;

    var input = ph.closest('form') ? ph.closest('form').querySelector('input[type="search"]') : null;
    
    // Check initial input value (e.g. on search results page)
    if (input && input.value && input.value.trim().length > 0) {
      ph.classList.add('is-hidden');
      return;
    }

    var keywordEl = ph.querySelector('.scrolling-keyword');
    if (!keywordEl) return;

    var idx = 0;
    keywordEl.textContent = '"' + keywords[0] + '"';

    setInterval(() => {
      if (input && input.value.trim().length > 0) {
        ph.classList.add('is-hidden');
        return;
      }

      idx = (idx + 1) % keywords.length;
      animateKeyword(keywordEl, '"' + keywords[idx] + '"');
    }, speed);
  });
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();

    if (this.input) {
      this.input.addEventListener(
        'input',
        FoxTheme.utils.debounce(this.onChange.bind(this), 300)
      );
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('click', this.onFocus.bind(this));
      this.input.addEventListener('blur', this.onBlur.bind(this));
      
      // Immediately hide placeholder if input has initial value
      if (this.input.value && this.input.value.trim().length > 0) {
        this.updatePlaceholderVisibility();
      }
    }

    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.clear.bind(this));
    }

    this.searchContent = this.querySelector('.search__content');
    this.desktopDropdown = this.querySelector('.desktop-search-dropdown');
    this.popularSearches = this.querySelector('[id^="PopularSearches"]');
    this.header = document.querySelector('header');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);

    if (this.searchProductTypes) {
      this.searchProductTypes.addEventListener('change', this.handleProductTypeChange.bind(this));
    }
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
    };
  }

  get input() { return this.querySelector('input[type="search"]'); }
  get resetButton() { return this.querySelector('button[type="reset"]'); }
  get searchProductTypes() { return this.querySelector('#SearchProductTypes'); }
  get form() { return this.querySelector('form'); }

  updatePlaceholderVisibility() {
    var ph = this.querySelector('.scrolling-placeholder');
    if (!ph) return;
    if (this.input && this.input.value.trim().length > 0) {
      ph.classList.add('is-hidden');
    } else {
      ph.classList.remove('is-hidden');
    }
  }

  onFocus() {
    this.updatePlaceholderVisibility();

    // Show desktop dropdown if present
    if (this.desktopDropdown) {
      this.desktopDropdown.classList.remove('hidden');
    }

    if (this.getQuery().length === 0) {
      // Show Popular Searches, hide live results
      if (this.popularSearches) this.popularSearches.style.display = '';
      if (this.searchContent) this.searchContent.classList.add('hidden');
      return;
    }

    this.renderSection(this.setupURL().toString());
  }

  onBlur() {
    this.updatePlaceholderVisibility();
  }

  getQuery() { return this.input ? this.input.value.trim() : ''; }

  clear(event) {
    if (event) event.preventDefault();
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.updatePlaceholderVisibility();
    if (this.popularSearches) this.popularSearches.style.display = '';
    if (this.searchContent) this.searchContent.classList.add('hidden');
    this.removeAttribute('results');
  }

  handleProductTypeChange() {
    if (this.getQuery().length > 0) {
      this.renderSection(this.setupURL().toString());
    }
  }

  onSubmit(event) {
    var productType = this.searchProductTypes ? this.searchProductTypes.value : null;
    if (!productType) return;
    var query = this.getQuery();
    if (query.length === 0 || query.startsWith('product_type:')) return;
    event.preventDefault();
    var params = new URLSearchParams();
    params.set('q', `product_type:${productType} AND ${query}`);
    params.set('options[prefix]', 'last');
    window.location.href = `${this.form.getAttribute('action')}?${params.toString()}`;
  }

  setupURL() {
    var url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    var search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value !== '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    url.searchParams.set('q', search_term);
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 6);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  onChange() {
    this.updatePlaceholderVisibility();
    if (this.getQuery().length === 0) {
      if (this.popularSearches) this.popularSearches.style.display = '';
      if (this.searchContent) this.searchContent.classList.add('hidden');
      return;
    }
    // Hide popular searches while showing live results
    if (this.popularSearches) this.popularSearches.style.display = 'none';
    this.renderSection(this.setupURL().toString());
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    this.renderSearchResults(this.cachedMap.get(url));
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);
    fetch(url)
      .then((r) => { if (!r.ok) throw new Error('Network error'); return r.text(); })
      .then((text) => { this.renderSearchResults(text); this.cachedMap.set(url, text); })
      .catch((err) => { console.error('Search error:', err); this.setAttribute('error', 'Failed'); })
      .finally(() => this.setLoadingState(false));
  }

  renderSearchResults(responseText) {
    var id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    var target = this.querySelector('[id^="PredictiveSearchResults-"]');
    if (!target) return;

    var parser = new DOMParser();
    var parsed = parser.parseFromString(responseText, 'text/html');
    var content = parsed.getElementById(id) || parsed.querySelector('.predictive-search-results-content') || parsed.querySelector('[data-predictive-search]');

    if (content) {
      target.innerHTML = content.innerHTML;
      if (this.searchContent) this.searchContent.classList.remove('hidden');
      if (this.popularSearches) this.popularSearches.style.display = 'none';
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      if (this.desktopDropdown) {
        this.desktopDropdown.classList.add('hidden');
      }
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
    } else {
      this.removeAttribute('loading');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

/* ─────────────────────────────────────────
   Mobile Search Popover & Global Handlers
───────────────────────────────────────── */
function initMobileSearchController() {
  var openedByTouch = false;

  function openMobileSearch() {
    var mobileDropdown = document.querySelector('.mobile-search-dropdown');
    if (!mobileDropdown) return;

    mobileDropdown.classList.remove('hidden');
    mobileDropdown.setAttribute('aria-hidden', 'false');
    var input = mobileDropdown.querySelector('input[type="search"]');
    if (input) {
      input.removeAttribute('disabled');
      input.readOnly = false;
      requestAnimationFrame(() => {
        input.focus({ preventScroll: true });
        if (typeof input.setSelectionRange === 'function') {
          var end = input.value.length;
          input.setSelectionRange(end, end);
        }
      });
    }
  }

  function closeMobileSearch() {
    var mobileDropdown = document.querySelector('.mobile-search-dropdown');
    if (!mobileDropdown) return;

    mobileDropdown.classList.add('hidden');
    mobileDropdown.setAttribute('aria-hidden', 'true');
  }

  document.addEventListener(
    'touchend',
    (e) => {
      var openBtn = e.target.closest('[data-open-mobile-search]');
      if (openBtn) {
        e.preventDefault();
        openedByTouch = true;
        openMobileSearch();
      }
    },
    { passive: false }
  );

  document.addEventListener(
    'touchend',
    (e) => {
      var input = e.target.closest('.mobile-search-dropdown input[type="search"]');
      if (input) {
        input.focus({ preventScroll: true });
      }
    },
    { passive: true }
  );

  document.addEventListener('click', (e) => {
    var input = e.target.closest('.mobile-search-dropdown input[type="search"]');
    if (input) {
      input.focus({ preventScroll: true });
    }
  });

  document.addEventListener('click', (e) => {
    // Open Mobile Search Trigger
    var openBtn = e.target.closest('[data-open-mobile-search]');
    if (openBtn) {
      e.preventDefault();
      if (openedByTouch) {
        openedByTouch = false;
        return;
      }
      var mobileDropdown = document.querySelector('.mobile-search-dropdown');
      if (mobileDropdown) {
        var isHidden = mobileDropdown.classList.contains('hidden');
        if (isHidden) {
          openMobileSearch();
        } else {
          closeMobileSearch();
        }
      }
      return;
    }

    // Close Mobile Search Trigger
    var closeBtn = e.target.closest('[data-close-mobile-search]');
    if (closeBtn) {
      e.preventDefault();
      closeMobileSearch();
      return;
    }

    // Click Outside to close mobile search
    var mobileDropdown = document.querySelector('.mobile-search-dropdown');
    if (mobileDropdown && !mobileDropdown.classList.contains('hidden')) {
      if (!e.target.closest('.mobile-search-dropdown') && !e.target.closest('[data-open-mobile-search]')) {
        closeMobileSearch();
      }
    }
  });

  // Desktop only: Close dropdown on significant scroll if search input is NOT focused
  var lastScrollY = window.scrollY;
  window.addEventListener(
    'scroll',
    () => {
      if (window.innerWidth >= 1024) {
        var desktopInput = document.querySelector('.header-search-input');
        if (document.activeElement !== desktopInput) {
          document.querySelectorAll('.desktop-search-dropdown').forEach((d) => d.classList.add('hidden'));
        }
      }
    },
    { passive: true }
  );

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.desktop-search-dropdown').forEach((d) => d.classList.add('hidden'));
      closeMobileSearch();
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initScrollingPlaceholders();
  initMobileSearchController();
});
